import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { CART_STORAGE_KEY } from '../constants/cart'
import {
  addAddonToCartLine,
  addCartLine,
  cartCompareAtTotal,
  cartItemCount,
  cartSavings,
  cartSubtotal,
  parseCartLines,
  removeCartLine,
  serializeCartLines,
  updateCartLineQuantity,
  type CartAddonInput,
  type CartLine,
  type CartLineInput,
} from '../helpers/cart'

type CartContextValue = {
  lines: CartLine[]
  itemCount: number
  subtotal: number
  compareAtTotal: number | null
  savings: number
  addItem: (input: CartLineInput) => void
  addAddon: (lineId: string, addon: CartAddonInput) => void
  setQuantity: (lineId: string, quantity: number) => void
  removeItem: (lineId: string) => void
  clear: () => void
}

const CartContext = createContext<CartContextValue | null>(null)

function readStoredCart(): CartLine[] {
  if (typeof window === 'undefined') return []
  return parseCartLines(window.localStorage.getItem(CART_STORAGE_KEY))
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => readStoredCart())

  useEffect(() => {
    window.localStorage.setItem(CART_STORAGE_KEY, serializeCartLines(lines))
  }, [lines])

  const value = useMemo<CartContextValue>(
    () => ({
      lines,
      itemCount: cartItemCount(lines),
      subtotal: cartSubtotal(lines),
      compareAtTotal: cartCompareAtTotal(lines),
      savings: cartSavings(lines),
      addItem: (input) => setLines((current) => addCartLine(current, input)),
      addAddon: (lineId, addon) =>
        setLines((current) => addAddonToCartLine(current, lineId, addon)),
      setQuantity: (lineId, quantity) =>
        setLines((current) => updateCartLineQuantity(current, lineId, quantity)),
      removeItem: (lineId) => setLines((current) => removeCartLine(current, lineId)),
      clear: () => setLines([]),
    }),
    [lines],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartContextValue {
  const value = useContext(CartContext)
  if (!value) {
    throw new Error('useCart must be used within CartProvider')
  }
  return value
}
